import { useState } from 'react';
import type {
  CyclingClassification,
  CyclingClassificationKind,
  CyclingStandingRow,
  CyclingStandings
} from '@h2h/shared';
import { cyclingGapLabel, flagEmoji, parseCyclingTimeSeconds } from '../../../lib/riderDisplay';
import { cn } from '../../../lib/cn';

// Time-based classifications rank by elapsed time, so they show the cycling
// gap-to-leader ("+2:22"); the rest (points / mountains) rank by points.
const TIME_BASED: CyclingClassificationKind[] = ['gc', 'stage', 'youth', 'teams'];

/**
 * Real race standings for a cycling league — the live ProCyclingStats
 * classifications (General classification + the jersey competitions + the latest
 * stage finish), scraped onto leagues/{id}/competition/cycling. Reference data
 * only: SEPARATE from the player's HeadToHead division (which the Compete tab owns).
 */
export function CyclingStandingsView({ standings }: { standings: CyclingStandings }) {
  const classifications = standings.classifications;
  const [active, setActive] = useState(classifications[0]?.kind ?? 'gc');
  const current = classifications.find((c) => c.kind === active) ?? classifications[0];
  const updated = new Date(standings.updatedAt).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short'
  });

  if (!current) return null;

  return (
    <>
      <div className="flex items-center justify-between pt-[18px] pb-3">
        <span className="text-[15px] font-extrabold tracking-[-0.02em] text-ink">
          {standings.raceName ?? 'Race standings'}
        </span>
        <span className="text-[12px] font-medium text-ink2">
          {standings.throughStageLabel ? `After ${standings.throughStageLabel} · ` : ''}
          {updated}
        </span>
      </div>

      {classifications.length > 1 && (
        <div className="no-scrollbar -mx-1 mb-3 flex gap-2 overflow-x-auto px-1 pb-0.5">
          {classifications.map((c) => (
            <ClassificationChip
              key={c.kind}
              label={c.label}
              active={c.kind === active}
              onClick={() => setActive(c.kind)}
            />
          ))}
        </div>
      )}

      <StandingsTable classification={current} />
    </>
  );
}

function ClassificationChip({
  label,
  active,
  onClick
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'tap shrink-0 rounded-full px-[14px] py-[7px] text-[12.5px] font-bold tracking-snug transition-colors',
        active ? 'bg-night text-white' : 'bg-fill text-ink2'
      )}
    >
      {label}
    </button>
  );
}

function StandingsTable({ classification }: { classification: CyclingClassification }) {
  const isTeams = classification.kind === 'teams';
  const timeBased = TIME_BASED.includes(classification.kind);
  // The leader sets the reference time everyone else's gap is measured against.
  const leaderSeconds = timeBased ? parseCyclingTimeSeconds(classification.rows[0]?.time) : null;

  const metricFor = (row: CyclingStandingRow, i: number): string => {
    if (timeBased) return cyclingGapLabel(row.time, leaderSeconds, i === 0);
    return row.points != null ? String(row.points) : '—';
  };

  return (
    <>
      <div className="px-2">
        <div className="flex items-center gap-[10px] pb-2">
          <span className="w-[18px] text-center text-[10px] font-bold text-ink3">#</span>
          <span className="flex-1 text-[10px] font-bold tracking-[0.04em] text-ink3">
            {isTeams ? 'TEAM' : 'RIDER'}
          </span>
          <span className="w-[78px] text-right text-[10px] font-bold text-ink3">
            {timeBased ? 'GAP' : 'PTS'}
          </span>
        </div>
      </div>
      <div className="overflow-hidden rounded-[20px] border border-black/7 bg-white">
        {classification.rows.map((row, i) => (
          <StandingRowView
            key={`${row.rank}-${row.riderUrl ?? row.teamName ?? i}`}
            row={row}
            leader={i === 0}
            metric={metricFor(row, i)}
          />
        ))}
      </div>
    </>
  );
}

function StandingRowView({
  row,
  leader,
  metric
}: {
  row: CyclingStandingRow;
  leader: boolean;
  metric: string;
}) {
  const name = (row.riderName ?? row.teamName ?? '—').trim();
  const subtitle = row.riderName ? row.teamName?.trim() || null : null; // team rows have no subtitle
  const flag = flagEmoji(row.nationality);
  const sameTime = metric === 's.t.';
  return (
    <div className="flex items-center gap-[10px] border-b border-black/5 px-[14px] py-[9px] last:border-b-0">
      <span className="num w-[18px] text-center text-[13px] font-extrabold text-ink">{row.rank}</span>
      {flag && <span className="text-[14px] leading-none">{flag}</span>}
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-bold tracking-[-0.01em] text-ink">{name}</div>
        {subtitle && <div className="truncate text-[11px] font-medium text-ink2">{subtitle}</div>}
      </div>
      <span
        className={cn(
          'num w-[78px] shrink-0 text-right text-[12px] tabular-nums',
          leader ? 'font-extrabold text-ink' : sameTime ? 'font-medium text-ink3' : 'font-semibold text-ink2'
        )}
      >
        {metric}
      </span>
    </div>
  );
}
